<script setup lang="ts">
/**
 * Højre filter-drawer — push-panel per D-012.
 *
 * Animerer mellem w-0 (lukket) og w-[300px] (åben). Indre indhold har fast
 * w-[300px] så det ikke reflower under bredde-animationen.
 *
 * Sticky til viewport. IKKE en modal — main-content forbliver interaktivt,
 * og charts inde i main re-flower via ResizeObserver (fase A10).
 *
 * Samme baggrund som resten af siden (bg-page) — kun den tynde lodrette
 * skillelinje markerer panelet, præcis som venstremenuen. Baggrunden sidder
 * på selve panelet (ikke kun arvet fra skallen) og panelet ligger i z-40,
 * over tabellernes sticky thead (30): en tabel der ikke kan krympe til sin
 * kolonne, løb ellers ud under panelet og blev tegnet OVEN PÅ det (Charlie
 * 28-09-2026 på Lagervarer, [D-334]).
 *
 * Filter-indholdet leveres af den aktive side via <FilterDrawerContent>
 * (teleport til #filter-drawer-body + registrering i useDrawer). Sider uden
 * indhold viser en ærlig placeholder — og draweren auto-lukker når en
 * filter-side forlades (se useDrawer).
 */

import { useDrawer } from '../composables/useDrawer';
import { ICONS } from '../icons';

const { isOpen, hasContent, close } = useDrawer();
</script>

<template>
  <aside
    class="sticky top-0 h-screen overflow-hidden transition-[width] duration-200 ease-out shrink-0 relative z-40 bg-page print:hidden"
    :class="isOpen ? 'w-[300px]' : 'w-0'"
    :aria-hidden="!isOpen"
  >
    <!-- Lodret skel på venstre kant (samme udtryk som venstremenuens) -->
    <div class="absolute left-0 top-0 w-px h-full bg-line/60" aria-hidden="true"></div>

    <!-- Fast-bredde indre så indholdet ikke reflower under animation -->
    <div class="w-[300px] flex flex-col h-full">
      <header class="flex items-center justify-between h-14 px-4">
        <h2 class="text-sm font-medium text-ink">Filtre</h2>
        <button
          type="button"
          class="flex items-center justify-center w-8 h-8 rounded-lg text-muted hover:bg-hover hover:text-ink transition-colors"
          aria-label="Luk filtre"
          @click="close"
        >
          <span class="block w-5 h-5" v-html="ICONS.close" />
        </button>
      </header>

      <!-- scrollbar-none: filterpanelet er kun 300px bredt, og en lodret
           scrollbar dér spiser plads og bryder den flade monochrome side.
           Panelet scroller stadig (lange filterlister som Marketing → Index og
           Regnskab → Index rækker under viewport-højden), scrollbaren er blot
           skjult — samme greb som venstremenuen og de brede tabeller
           (Charlies rettelse 27-08-2026). Bundmargenen lægger mobilens
           bundmenu til (0 på desktop), ellers ligger det nederste valg under
           menuen og kan ikke nås ([D-489]). -->
      <div class="flex-1 overflow-y-auto scrollbar-none px-4 pb-[calc(1rem+var(--mobile-nav-h))]">
        <!-- Sidernes FilterDrawerContent teleporterer herind -->
        <div id="filter-drawer-body"></div>
        <p v-if="!hasContent" class="text-sm text-muted">
          Denne side har ingen filtre i panelet.
        </p>
      </div>
    </div>
  </aside>
</template>
